import { validateAndFormat } from "../../common/validateAndFormat";
import { ERROR_MESSSAGE } from "../../config";
import { generatedFormattedDate } from "../../common/dateAndTime";
import { refresh } from "../../common/refresh";
import { setOrPush, pushOrUnshiftOfficeLocation } from "./helpers";
import { dateAndTimeMixin } from "../../../mixins/dateAndTimeMixin";

/**
 * Check weather check time is between start time and end time
 *
 * @param {*} checkTime
 * @param {*} startTime
 * @param {*} endTime
 */
function isTimeBetweenStartAndEnd(checkTime, startTime, endTime) {
  // Check if check time is greater than start time
  if (
    checkTime.hr > startTime.hr ||
    (checkTime.hr === startTime.hr && checkTime.min >= startTime.min)
  ) {
    // Check if check time is less than end time
    if (
      checkTime.hr < endTime.hr ||
      (checkTime.hr === endTime.hr && checkTime.min <= endTime.min)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Check if there is any time overlap
 *
 * @param {Object} appointment
 * @param {Object} requestData
 */
function overlapTimeCheck(appointment, requestData) {
  // Step 1: Get appointment start date and end date
  const appStartTime = dateAndTimeMixin.methods.get24HrTime(
    appointment.app_start_hh,
    appointment.app_start_mm,
    appointment.app_start_tt
  );
  const appEndTime = dateAndTimeMixin.methods.get24HrTime(
    appointment.app_end_hh,
    appointment.app_end_mm,
    appointment.app_end_tt
  );
  const reqStartTime = requestData.time.start_time;
  const reqEndTime = requestData.time.end_time;

  // Check for overlap - new appointment start time is between start time and end time of old appointment
  if (isTimeBetweenStartAndEnd(reqStartTime, appStartTime, appEndTime)) {
    return true;
  }

  // Check overlap - new appointment has end time between start time and end time of old appointment
  if (isTimeBetweenStartAndEnd(reqEndTime, appStartTime, appEndTime)) {
    return true;
  }

  // Default return
  return false;
}

/**
 * Check if appointment is over lapping appointment
 * Return true if overlap, no if no overlap
 *
 * @param {Object} context
 * @param {Object} requestData
 */
async function checkForOverLap(context, requestData, selectedProviderId) {
  // Step 1: Format the date
  const formattedDate = dateAndTimeMixin.methods.formatDate({
    dayOfMonth: requestData.date.day_of_month,
    month: requestData.date.month - 1,
    year: requestData.date.year
  });

  // Step 2: Get the already queried appointments
  const alreadyQueriedDates =
    selectedProviderId in context.getters.getAlreadyQueriedDates
      ? context.getters.getAlreadyQueriedDates[selectedProviderId]
      : [];

  // Step 3: Check if formatted Date is already queried
  if (alreadyQueriedDates.indexOf(formattedDate) === -1) {
    // Set the date
    context.commit("setYear", requestData.date.year);
    context.commit("setMonth", requestData.date.month - 1);
    context.commit("setDayOfMonth", requestData.date.day_of_month);

    // Fetch the appointment
    await context.dispatch("getAppointmentDetails");
  }

  // Step 4: Check for overlapping appointment
  const allAppointments = context.getters.getAllAppointments;
  if (!(formattedDate in allAppointments)) {
    return false;
  }
  const appointments = allAppointments[formattedDate];
  let overLap = false;
  for (let i = 0; i < appointments.length; i++) {
    overLap = overLap || overlapTimeCheck(appointments[i], requestData);
    if (overLap) {
      break;
    }
  }

  // Step 2: Check if date has been queried
  return overLap;
}

/**
 * Actions for add appointment
 */
export const actions = {
  /**
   * Getting form details
   *
   * @param {Object} context
   */
  async getFormDetails(context) {
    // Step 1: Check if details have already been fetched
    if (context.state.appointmentType.fetched) {
      return context.commit("setAppointmentTimeIncrement");
    }

    // Step 2: Loading start
    context.commit("setLoadingAddAppointment", true);

    // Step 3: Fetch data
    const json = await context.dispatch("makeAuthRequest", {
      url: "appointment/form_details",
      method: "GET"
    });
    // const listjson = await context.dispatch("makeAuthRequest", {
    //   url: "get_permitted_providers",
    //   method: "GET"
    // });
    // // alert(listjson)
    // console.log(listjson);
    // Step 4: Check if we have any error
    if ("error" in json) {
      // Stop loading
      return context.commit("setLoadingAddAppointment", false);
    }

    // Step 5: Filtering data for appointment
    const otherEvent = {};
    const groupAppointment = {};
    const eventAppointment = {};
    const person = {};
    const appUsers = {};
    const subscribers = [];
    const { group_users: groupUsers } = json;
    for (let i = 0; i < json.details.length; i++) {
      switch (json.details[i].type) {
        case "app_user":
          setOrPush(appUsers, {
            key: json.details[i]["provider"],
            val: json.details[i]
          });
          break;

        case "other_event":
          setOrPush(otherEvent, {
            key: json.details[i]["provider"],
            val: json.details[i]
          });
          break;

        case "group_appointment":
          setOrPush(groupAppointment, {
            key: json.details[i].provider,
            val: { ...json.details[i], users: groupUsers[json.details[i].id] }
          });
          break;

        case "event_appointment":
          setOrPush(eventAppointment, {
            key: json.details[i]["provider"],
            val: json.details[i]
          });
          break;

        case "person":
          setOrPush(person, {
            key: json.details[i]["provider"],
            val: json.details[i]
          });
          break;

        case "subscriber":
          subscribers.push(json.details[i]);
          break;

        default:
          break;
      }
    }
    // Step 6: Commit data to the state
    context.commit("setTypesAppointmentTypes", {
      otherEvent,
      groupAppointment,
      eventAppointment,
      person,
      appUsers,
      subscribers
    });

    // Step 7: Setting office locations
    const locationDetails = {};
    for (let i = 0; i < json["office_locations"].length; i++) {
      pushOrUnshiftOfficeLocation(locationDetails, {
        defaultOfficeId: json["provider_address_id"],
        officeLocation: json["office_locations"][i]
      });
    }
    // Step 8: Commiting locations
    context.commit("setLocations", {
      locationDetails
    });

    // Step 9: Commiting the provider
    if ("providers" in json) {
      json.providers = json.providers === false ? [] : json.providers;
      if (context.getters.getUserRole !== 24) {
        const currentUserDetails = context.getters.getUserDetails;
        context.commit("setProviders", [currentUserDetails, ...json.providers]);
      } else {
        context.commit("setProviders", [...json.providers]);
      }
    }

    // Step 10: Commiting how much time to increment
    if ("appointment_time_increment" in json) {
      context.commit(
        "setAppointmentTimeIncrement",
        json["appointment_time_increment"]
      );
    }

    // Step 11: Loading end
    context.commit("setLoadingAddAppointment", false);
  },
  /**
   * Getting form details
   *
   * @param {Object} context
   */
  async getFormDetailsPersonOther(context) {
    // Step 1: Check if details have already been fetched
    // if (context.state.appointmentType.fetched) {
    //   return context.commit("setAppointmentTimeIncrement");
    // }

    // Step 2: Loading start
    // context.commit("setLoadingAddAppointment", true);

    // Step 3: Fetch data
    const json = await context.dispatch("makeAuthRequest", {
      url: "appointment/form_details",
      method: "GET"
    });
    // const listjson = await context.dispatch("makeAuthRequest", {
    //   url: "get_permitted_providers",
    //   method: "GET"
    // });
    // // alert(listjson)
    // console.log(listjson);
    // Step 4: Check if we have any error
    if ("error" in json) {
      // Stop loading
      return context.commit("setLoadingAddAppointment", false);
    }

    // Step 5: Filtering data for appointment
    const otherEvent = {};
    const groupAppointment = {};
    const eventAppointment = {};
    const person = {};
    const appUsers = {};
    const subscribers = [];
    const { group_users: groupUsers } = json;
    for (let i = 0; i < json.details.length; i++) {
      switch (json.details[i].type) {
        case "app_user":
          setOrPush(appUsers, {
            key: json.details[i]["provider"],
            val: json.details[i]
          });
          break;

        case "other_event":
          setOrPush(otherEvent, {
            key: json.details[i]["provider"],
            val: json.details[i]
          });
          break;

        case "group_appointment":
          setOrPush(groupAppointment, {
            key: json.details[i].provider,
            val: { ...json.details[i], users: groupUsers[json.details[i].id] }
          });
          break;

        case "event_appointment":
          setOrPush(eventAppointment, {
            key: json.details[i]["provider"],
            val: json.details[i]
          });
          break;

        case "person":
          setOrPush(person, {
            key: json.details[i]["provider"],
            val: json.details[i]
          });
          break;

        case "subscriber":
          subscribers.push(json.details[i]);
          break;

        default:
          break;
      }
    }
    // Step 6: Commit data to the state
    context.commit("setTypesAppointmentTypes", {
      otherEvent,
      groupAppointment,
      eventAppointment,
      person,
      appUsers,
      subscribers
    });

    // Step 7: Setting office locations
    const locationDetails = {};
    for (let i = 0; i < json["office_locations"].length; i++) {
      pushOrUnshiftOfficeLocation(locationDetails, {
        defaultOfficeId: json["provider_address_id"],
        officeLocation: json["office_locations"][i]
      });
    }
    // Step 8: Commiting locations
    context.commit("setLocations", {
      locationDetails
    });

    // Step 9: Commiting the provider
    if ("providers" in json) {
      json.providers = json.providers === false ? [] : json.providers;
      if (context.getters.getUserRole !== 24) {
        const currentUserDetails = context.getters.getUserDetails;
        context.commit("setProviders", [currentUserDetails, ...json.providers]);
      } else {
        context.commit("setProviders", [...json.providers]);
      }
    }

    // Step 10: Commiting how much time to increment
    if ("appointment_time_increment" in json) {
      context.commit(
        "setAppointmentTimeIncrement",
        json["appointment_time_increment"]
      );
    }

    // Step 11: Loading end
    context.commit("setLoadingAddAppointment", false);
  },

  /**
   * When add appointment form is submitted
   *
   * @param {Object} context
   */
  async submitForm(context) {
    // Step 1: validating and formatting data
    const date = validateAndFormat.date(context.state.date, context);
    const time = validateAndFormat.time(
      context.state.startTime,
      context.state.endTime,
      context
    );
    const providerId = validateAndFormat.provider(context.state.provider);
    const appointmentType = validateAndFormat.appointmentType(
      context.state.appointmentType,
      providerId,
      context
    );
    const name = context.rootGetters["addNew/getName"];
    const firstName = validateAndFormat.name(name.firstName);
    const repeat = validateAndFormat.repeat(context.state.repeat);
    const editID = validateAndFormat.editID(context.state.edit);
    const oneAllFuture = validateAndFormat.oneAllFuture(context.state.edit);
    const repeatDate = validateAndFormat.repeatDate(
      context.state.startRepeat,
      context.state.endRepeat,
      context,
      {
        isEdit: context.state.edit.isEdit,
        editID,
        oneAllFuture,
        date
      }
    );
    const officeLocation = validateAndFormat.officeLocation(
      context.state.officeLocation,
      providerId,
      context
    );
    const notes = validateAndFormat.notes(context.state.notes);
    const isAllDay = context.state.allDay;
    const isVideo = context.state.isVideo;
    const isOffice = context.state.isOffice;
    const isPhone = context.state.isPhone;
    const isOther = context.getters.getIsOther;
    const selectedProviderId = context.getters.getSelectedProviderID;
    const usePsybooksVideo = validateAndFormat.usePsybooksVideo({
      usePsybooksVideo: context.getters.getUsePsybooksVideo,
      isVideo: isVideo,
      appointmentIsEvent: appointmentType
        ? appointmentType.type === "event_appointment"
        : false,
      reminder: context.getters.getVideoReminder,
      date,
      time,
      context,
      appointmentType: context.state.appointmentType,
      selectedProviderId
    });
    const useConnectVideo = usePsybooksVideo.date && context.getters.getUseConnectVideo;
    let isEdit = false;

    // Step 1.1 Getting current date so that in edit appointment, appointment can be deleted if date is changed
    const selectedDate = generatedFormattedDate(
      context.getters.getSelectedDayOfMonth,
      context.getters.getSelectedMonth + 1,
      context.getters.getSelectedYear
    );

    // Start spinner here for both add appointment and get appointment
    context.commit("setLoadingAddAppointment", true);
    context.commit("increaseLoadingGetAppointment");

    if (
      date &&
      time &&
      appointmentType &&
      repeat &&
      repeatDate &&
      officeLocation &&
      editID &&
      oneAllFuture &&
      providerId &&
      (!usePsybooksVideo || usePsybooksVideo.date)
    ) {
      // Step 2: Making request data json
      const requestData = {
        is_all_day: isAllDay,
        is_video: isVideo,
        is_office: isOffice,
        is_phone: isPhone,
        is_other: isOther,
        date: {
          day_of_month: date.dayOfMonth,
          month: date.month + 1,
          year: date.year
        },
        time: {
          start_time: {
            hr: time.startTime.hr,
            min: time.startTime.min,
            sec: time.startTime.sec
          },
          end_time: {
            hr: time.endTime.hr,
            min: time.endTime.min,
            sec: time.endTime.sec
          }
        },
        appointmentType: {
          id: appointmentType.id,
          type: appointmentType.type,
          name: appointmentType.id === 0 ? firstName : ""
        },
        repeat: {
          info: {
            option: repeat.option,
            custom: repeat.custom,
            weeklyOn: repeat.weeklyOn
          },
          startDate:
            repeat.option === "Never"
              ? false
              : {
                  dayOfMonth: repeatDate.startDate.dayOfMonth,
                  month: repeatDate.startDate.month + 1,
                  year: repeatDate.startDate.year
                },
          endDate:
            repeat.option === "Never"
              ? false
              : repeatDate.endDate.never === true
              ? {
                  never: true
                }
              : {
                  never: false,
                  dayOfMonth: repeatDate.endDate.dayOfMonth,
                  month: repeatDate.endDate.month + 1,
                  year: repeatDate.endDate.year
                }
        },
        provider_id: providerId,
        notes: notes,
        psybooks_video: usePsybooksVideo,
        connect_video: useConnectVideo,
        officeLocation:
          officeLocation.custom === true
            ? {
                custom: true,
                value: officeLocation.location
              }
            : {
                custom: false,
                value: {
                  id: officeLocation.location["address_id"]
                }
              }
      };

      // Step A: Check for overlap
      if (
        editID === true &&
        context.getters.getOverLapCheck !== "ALLOWED" &&
        (await checkForOverLap(context, requestData, selectedProviderId))
      ) {
        context.commit("setLoadingAddAppointment", false); // Stop loading spinner in add appointment page
        context.commit("decreaseLoadingGetAppointment"); // Stop loading spinner in get appointment page
        return context.commit("setOverLapCheck", "ASK_USER");
      }

      // Checking if edit or add
      let method = "POST";
      let url = "appointment/add";

      if (context.state.edit.isEdit) {
        isEdit = true;
        method = "PUT";
        url = "appointment/edit";
        requestData["editID"] = editID;
        requestData["oneAllFuture"] = oneAllFuture;
      }

      // Changing the current selected day month and year to the date on which appointment was added
      context.commit("setYear", date.year);
      context.commit("setMonth", date.month);
      context.commit("setDayOfMonth", date.dayOfMonth);
      context.commit("setWeek", date.dayOfMonth);

      // Form submitted
      if (requestData.repeat.info.option !== "Never") {
        // Prevent the data fetching in case there is a repeat to prevent fetching multiple times
        const toDate = generatedFormattedDate(
          date.dayOfMonth,
          date.month + 1,
          date.year
        );
        context.commit("addDateToAlreadyQueriedDetails", {
          date: toDate,
          selectedProviderId
        });
      }
      context.commit("setSubmitted", true);

      let res = await context.dispatch("makeAuthRequest", {
        url,
        method,
        data: requestData
      });

      // If error is found in res, stop executing the function
      if ("error" in res) {
        if (res.code === 400) {
          context.dispatch(
            "globalMessagePopup",
            ERROR_MESSSAGE.videoAppointmentResponse
          );
        } else if (res.code === 422) {
          context.dispatch(
            "globalMessagePopup",
            res.error
          );
        } else {
          context.dispatch("globalMessagePopup", ERROR_MESSSAGE[500]);
        }
      }

      // Only setting the value if data has already been fetched for that date.
      else if (res.res !== false) {
        // Remove the same appointment if added before for edge case
        context.commit("removeFromAppointmentList", {
          date: selectedDate,
          id: isNaN(res.res.id) ? 0 : parseInt(res.res.id)
        });

        if (isEdit) {
          // Refresh year for edge cases
          let year = res.res.app_date.split("-")[0];
          year = isNaN(year) ? false : parseInt(year);
          if (year) {
            context.commit("refreshYears", {
              years: [year],
              selectedProviderId
            });
          }

          context.dispatch("getYearlyAppointments", date.year);
        }

        // Adding the appointment
        context.commit("addOrderedNextAppointmentForDate", {
          appointment: res.res,
          selectedProviderId,
          isEdit
        });
        context.commit("addDetails", res.res);
      } else {
        // If delete is required then we delete the appointments
        if ("delete_required" in res && res["delete_required"] !== false) {
          refresh.deleteRequired(
            res["delete_required"],
            context,
            selectedProviderId
          );
        }

        // If refresh is required we refresh the dates
        if (res["refresh_required"] !== false) {
          refresh.refreshRequired(
            res["refresh_required"],
            context,
            selectedProviderId
          );
        }

        // Refresh total is first deleting and then refreshing
        if ("refresh_total" in res && res["refresh_total"] !== false) {
          // Step 1: Delete the appointments
          refresh.deleteRequired(
            res["refresh_total"],
            context,
            selectedProviderId
          );

          // Step 2: Refresh the dates
          refresh.refreshRequired(
            res["refresh_total"].map(appointment => {
              return appointment["app_date"];
            }),
            context,
            selectedProviderId
          );

          // Fetch the yearly dates
          context.dispatch("getYearlyAppointments", date.year);
        }
      }
    }

    // Stop spinner here
    context.commit("setLoadingAddAppointment", false); // Stop loading spinner in add appointment page
    context.commit("decreaseLoadingGetAppointment"); // Stop loading spinner in get appointment page
  }
};
